import { useCallback, useEffect, useState } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';

const THEME_STORAGE_KEY = 'pc-theme';

function readStoredPreference(): ThemePreference {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch (error) {
    console.warn('[theme] localStorage indisponível; usando tema do sistema.', error);
  }
  return 'system';
}

function applyPreference(preference: ThemePreference) {
  const root = document.documentElement;
  if (preference === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', preference);
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Preferência de tema: claro, escuro ou do sistema (padrão). */
export function useTheme() {
  const [preference, setPreference] = useState<ThemePreference>(readStoredPreference);
  const [isSystemDark, setIsSystemDark] = useState(systemPrefersDark);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event: MediaQueryListEvent) => setIsSystemDark(event.matches);
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, []);

  const changePreference = useCallback((next: ThemePreference) => {
    setPreference(next);
    applyPreference(next);
    try {
      if (next === 'system') localStorage.removeItem(THEME_STORAGE_KEY);
      else localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch (error) {
      console.warn('[theme] não foi possível salvar a preferência de tema.', error);
    }
  }, []);

  const isDark = preference === 'dark' || (preference === 'system' && isSystemDark);

  return { preference, isDark, setPreference: changePreference };
}
